import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { buildsGridConfig } from '@/features/builds/grid/gridConfig';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import {
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { buildTagColorByName } from '@/features/shared/tags';
import { useTagOptions } from '@/shared/hooks';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { buildBuildsColumns } from './buildsColumns';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { DataGridProps, GridColDef, GridFilterModel, GridSortModel } from '@/ui/primitives';
import type { BuildDto, QueryResult } from '@flowqa/shared';

type BuildsTableProps = {
  fixedFilters?: GridQueryInput['filters'];
  noRowsText?: string;
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  hideFooter?: boolean;
  disableColumnSorting?: boolean;
  disableColumnMenu?: boolean;
  showToolbar?: boolean;
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<BuildDto>>;
  queryKey?: QueryKey;
  onRowsChange?: (rows: BuildDto[]) => void;
  onQueryMeta?: (meta: { total: number; items: BuildDto[] } | null) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  showPipelineColumn?: boolean;
  search?: string;
  columnVisibilityModel?: Record<string, boolean>;
  onColumnVisibilityModelChange?: DataGridProps['onColumnVisibilityModelChange'];
};

export const BuildsTable = ({
  fixedFilters,
  noRowsText = 'No builds found',
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting = false,
  disableColumnMenu = false,
  showToolbar = true,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  onRowsChange,
  onQueryMeta,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  showPipelineColumn = true,
  search,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
}: BuildsTableProps): ReactElement => {
  const { session } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { tagOptions } = useTagOptions({ projectId: selectedProjectId });
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const columns: Array<GridColDef<BuildDto>> = useMemo(
    () =>
      buildBuildsColumns({
        showPipelineColumn,
        durationThreshold: thresholds.pipelines.avgDuration,
        tagColorByName,
      }),
    [showPipelineColumn, tagColorByName, thresholds.pipelines.avgDuration],
  );
  const togglableColumnFields = useMemo(() => columns.map((column) => column.field), [columns]);

  const gridConfig = buildsGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey === undefined ? 'builds-table' : persistKey,
    fixedFilters,
    initialSortModel,
    initialFilterModel,
  });

  return (
    <ServerDataTable<BuildDto>
      queryPath="builds/query"
      {...gridConfig}
      queryFn={queryFn ?? gridConfig.queryFn}
      queryKey={queryKey ?? gridConfig.queryKey}
      columns={columns}
      getRowId={(row) => row.id}
      token={session?.tokens.accessToken ?? null}
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      pageSizeOptions={pageSizeOptions}
      initialPageSize={initialPageSize}
      search={search}
      columnVisibilityModel={columnVisibilityModel}
      disablePageSizeSelect={disablePageSizeSelect}
      hideFooter={hideFooter}
      disableColumnMenu={disableColumnMenu}
      disableColumnSorting={disableColumnSorting}
      showToolbar={showToolbar}
      showColumnVerticalBorder={false}
      showCellVerticalBorder={false}
      noRowsText={noRowsText}
      disableCellFocus
      getRowHeight={() => 'auto'}
      skeletonRowHeight={TABLE_ROW_HEIGHT}
      skeletonGetRowHeight={null}
      showSkeletonOnFetch
      showLastUpdated={showLastUpdated}
      autoHeight
      onRowsChange={onRowsChange}
      onQueryMeta={onQueryMeta}
      onQueryInput={onQueryInput}
      onDataUpdatedAt={onDataUpdatedAt}
      gridProps={{
        getEstimatedRowHeight: () => TABLE_ROW_MEASURE_HEIGHT,
        onColumnVisibilityModelChange,
        slotProps: {
          toolbar: {
            showQuickFilter: true,
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
          },
          columnsManagement: {
            disableShowHideToggle: true,
            disableResetButton: true,
            getTogglableColumns: () => togglableColumnFields,
          },
        },
      }}
      sx={[
        ...(Array.isArray(tableSx) ? tableSx : [tableSx]),
        {
          '& .MuiDataGrid-row': {
            minHeight: `${TABLE_ROW_MEASURE_HEIGHT}px !important`,
          },
          '& .MuiDataGrid-cell': {
            minHeight: `${TABLE_ROW_MEASURE_HEIGHT}px !important`,
          },
          '& .MuiDataGrid-cell[data-field="buildKey"], & .MuiDataGrid-cell[data-field="status"]': {
            alignItems: 'flex-start',
          },
        },
      ]}
    />
  );
};
